import { db, auth } from '../../firebaseconfig';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc, getDoc, writeBatch, query, where } from 'firebase/firestore';

export const fetchColumns = async () => {
  try {
    const user = auth.currentUser;
    if (user) {
      const columnsCollection = collection(db, 'kanban_columns');
      const q = query(columnsCollection, where("uid", "==", user.uid));
      const columnsSnapshot = await getDocs(q);
      const columnsList = columnsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => a.order - b.order);
      return columnsList;
    }
  } catch (error) {
    console.error("Error fetching columns:", error);
  }
};

export const fetchTasks = async () => {
  try {
    const user = auth.currentUser;
    if (user) {
      const tasksCollection = collection(db, 'kanban_tasks');
      const q = query(tasksCollection, where("uid", "==", user.uid));
      const tasksSnapshot = await getDocs(q);
      const tasksList = tasksSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }));
      return tasksList;
    }
  } catch (error) {
    console.error("Error fetching tasks:", error);
  }
};

export const addOrUpdateTask = async (task, editingTaskId = null) => {
  const user = auth.currentUser;
  if (!task.title || !task.description || !task.columnId || !user) {
    throw new Error("Información de tarea incompleta o usuario no autenticado");
  }

  try {
    if (editingTaskId) {
      const taskRef = doc(db, 'kanban_tasks', editingTaskId);
      await updateDoc(taskRef, { ...task, uid: user.uid });
      return { id: editingTaskId, ...task, uid: user.uid };
    } else {
      const tasksInColumn = await fetchTasks();
      const newOrder = tasksInColumn.filter(t => t.columnId === task.columnId).length + 1;
      const docRef = await addDoc(collection(db, 'kanban_tasks'), { 
        ...task, 
        uid: user.uid, 
        order: newOrder 
      });
      return { id: docRef.id, ...task, uid: user.uid, order: newOrder };
    }
  } catch (error) {
    console.error("Error adding/editing task: ", error);
    throw error;
  }
};

export const deleteTask = async (id) => {
  try {
    const user = auth.currentUser;
    const taskRef = doc(db, 'kanban_tasks', id);
    const taskSnapshot = await getDoc(taskRef);

    if (taskSnapshot.exists() && taskSnapshot.data().uid === user.uid) {
      await deleteDoc(taskRef);
    } else {
      throw new Error("No tienes permisos para eliminar esta tarea.");
    }
  } catch (error) {
    console.error("Error deleting task:", error);
    throw error;
  }
};

export const addColumn = async (columnName, color) => {
  if (typeof columnName !== 'string' || !columnName.trim()) {
    throw new Error("El nombre de la columna debe ser una cadena válida.");
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error("Usuario no autenticado");
  }

  try {
    const columnsCollection = collection(db, 'kanban_columns');
    const columnsSnapshot = await getDocs(query(columnsCollection, where("uid", "==", user.uid)));
    const columnsList = columnsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const newOrder = columnsList.length > 0
      ? Math.max(...columnsList.map(col => col.order)) + 1
      : 1;

    const newColumnRef = await addDoc(columnsCollection, {
      name: columnName,
      uid: user.uid,
      color,
      order: newOrder,
    });

    return { id: newColumnRef.id, name: columnName, color, order: newOrder };
  } catch (error) {
    console.error("Error adding column: ", error);
    throw error;
  }
};

export const deleteColumn = async (columnId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Usuario no autenticado");
    }

    const batch = writeBatch(db);
    
    // Eliminar la columna
    const columnRef = doc(db, 'kanban_columns', columnId);
    batch.delete(columnRef);

    // Obtener y eliminar todas las tareas asociadas a la columna
    const tasksCollection = collection(db, 'kanban_tasks');
    const tasksSnapshot = await getDocs(query(tasksCollection, where("columnId", "==", columnId), where("uid", "==", user.uid)));
    
    tasksSnapshot.docs.forEach(task => {
      const taskRef = doc(db, 'kanban_tasks', task.id);
      batch.delete(taskRef);
    });

    // Ejecutar el batch
    await batch.commit();

    console.log(`Columna con ID "${columnId}" y sus tareas asociadas eliminadas con éxito.`);
  } catch (error) {
    console.error("Error al eliminar la columna:", error);
    throw error;
  }
};

export const editColumnName = async (columnId, newName) => {
  if (!newName.trim()) return;

  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Usuario no autenticado");
    }

    const columnRef = doc(db, 'kanban_columns', columnId);
    await updateDoc(columnRef, { name: newName });

  } catch (error) {
    console.error("Error updating column name:", error);
    throw error;
  }
};

export const moveColumn = async (columns, dragIndex, hoverIndex) => {
  const reorderedColumns = [...columns];
  const [removedColumn] = reorderedColumns.splice(dragIndex, 1);
  reorderedColumns.splice(hoverIndex, 0, removedColumn);

  try {
    const batch = writeBatch(db);
    reorderedColumns.forEach((column, index) => {
      const columnRef = doc(db, 'kanban_columns', column.id);
      batch.update(columnRef, { order: index });
    });

    await batch.commit();
    return reorderedColumns;
  } catch (error) {
    console.error("Error updating column order:", error);
    throw error;
  }
};

